// Storage explorer ("Ver archivos", integration contract §8).
// The storage account is only configured in Contracts: the front lists folders/files and asks
// for a read-only SAS there, then opens/downloads the blob straight from Azure with that SAS.
import { UploadSasToken } from '../models/Entities/AzureFiles/AzureFileUpload';
import {
  StorageBrowseRequest,
  StorageBrowseViewModel,
  StorageCreateFolderRequest,
  StorageFolderRenameViewModel,
  StorageFolderViewModel,
  StorageReadSasRequest,
  StorageRenameFolderRequest,
  StorageUploadSasRequest,
} from '../models/Entities/AzureFiles/StorageBrowser';
import { ErrorType } from '../models/HttpError';
import { IResult } from '../models/HttpResult';

//Functions
import * as Functions from '../functions/common';

// Error Tracer
import * as Sentry from '@sentry/react';

const Gateway = process.env.REACT_APP_BILLER_API_GATEWAY;

// The gateway maps /contracts/{everything} → Contracts API /contracts/{everything}.
export const StorageBrowseURL = (request: StorageBrowseRequest): string => {
  const query = new URLSearchParams();
  query.set('path', request.path ?? '');
  if (request.continuationToken) query.set('continuationToken', request.continuationToken);
  if (request.pageSize !== undefined) query.set('pageSize', String(request.pageSize));
  return `${Gateway}contracts/files/storage/browse?${query.toString()}`;
};
export const ReadSasTokenURL = (): string => `${Gateway}contracts/files/sas-token/read`;
export const ContractFileReadSasTokenURL = (fileId: number): string => `${Gateway}contracts/files/${fileId}/sas-token/read`;
// Management (contract §9.2)
export const StorageFoldersURL = (): string => `${Gateway}contracts/files/storage/folders`;
export const StorageRenameFolderURL = (): string => `${Gateway}contracts/files/storage/folders/rename`;
export const StorageUploadSasURL = (): string => `${Gateway}contracts/files/storage/sas-token/upload`;
export const StorageDeleteFileURL = (path: string): string =>
  `${Gateway}contracts/files/storage/files?${new URLSearchParams({ path }).toString()}`;
export const StorageDeleteFolderURL = (path: string): string =>
  `${Gateway}contracts/files/storage/folders?${new URLSearchParams({ path }).toString()}`;

const buildError = (code: number, message: string, errors: string[] = []): ErrorType => ({
  ErrorCode: code,
  ErrorMessage: message,
  Errors: errors,
});

const toErrorType = async (response: Response): Promise<ErrorType> => {
  const body = await response.text().catch(() => '');
  return Functions.HttpErrorHandler(body || null, response);
};

const isErrorType = (error: unknown): error is ErrorType =>
  typeof error === 'object' && error !== null && 'ErrorCode' in error;

const normalizeError = (error: unknown): ErrorType => {
  if (isErrorType(error)) return error;
  const message = error instanceof Error ? error.message : 'Network error';
  return buildError(0, message);
};

const headers = (token: string): HeadersInit => ({
  Accept: 'application/json',
  'Content-Type': 'application/json',
  Authorization: 'Bearer ' + token,
});

/** GET contracts/files/storage/browse → folders first (by name) and files (lastModified desc). */
export const browseStorage = async (token: string, request: StorageBrowseRequest): Promise<StorageBrowseViewModel> => {
  try {
    const response = await fetch(StorageBrowseURL(request), { method: 'GET', headers: headers(token) });
    if (!response.ok) {
      Sentry.captureException(response);
      throw await toErrorType(response);
    }
    const result = (await response.json()) as IResult<StorageBrowseViewModel>;
    const data = result?.data;
    if (!data) {
      throw buildError(response.status, result?.message || 'Empty storage listing', result?.errors ?? []);
    }
    return {
      path: data.path ?? request.path ?? '',
      breadcrumbs: data.breadcrumbs ?? [],
      folders: data.folders ?? [],
      files: data.files ?? [],
      continuationToken: data.continuationToken ?? null,
      currentFolder: data.currentFolder ?? null,
    };
  } catch (error: unknown) {
    Sentry.captureException(error);
    throw normalizeError(error);
  }
};

/** POST contracts/files/sas-token/read → read-only SAS (inline or attachment). */
export const requestReadSas = async (token: string, request: StorageReadSasRequest): Promise<UploadSasToken> => {
  try {
    const response = await fetch(ReadSasTokenURL(), {
      method: 'POST',
      headers: headers(token),
      body: JSON.stringify(request),
    });
    if (!response.ok) {
      Sentry.captureException(response);
      throw await toErrorType(response);
    }
    const result = (await response.json()) as IResult<UploadSasToken>;
    if (!result?.succeeded || !result?.data?.sasUrl) {
      throw buildError(response.status, result?.message || 'SAS token not received', result?.errors ?? []);
    }
    return result.data;
  } catch (error: unknown) {
    Sentry.captureException(error);
    throw normalizeError(error);
  }
};

/**
 * POST contracts/files/{fileId}/sas-token/read → read-only SAS of a contract file by id. Legacy files stored in the
 * database are copied by the API to the tenant storage on the first call, so every contract file opens with the same viewer.
 */
export const requestContractFileReadSas = async (token: string, fileId: number, download: boolean): Promise<UploadSasToken> => {
  try {
    const response = await fetch(ContractFileReadSasTokenURL(fileId), {
      method: 'POST',
      headers: headers(token),
      body: JSON.stringify({ download }),
    });
    if (!response.ok) {
      Sentry.captureException(response);
      throw await toErrorType(response);
    }
    const result = (await response.json()) as IResult<UploadSasToken>;
    if (!result?.succeeded || !result?.data?.sasUrl) {
      throw buildError(response.status, result?.message || 'SAS token not received', result?.errors ?? []);
    }
    return result.data;
  } catch (error: unknown) {
    Sentry.captureException(error);
    throw normalizeError(error);
  }
};

/** Error code used when the browser could not read the blob (network failure or CORS not enabled). */
export const BLOB_NETWORK_ERROR_CODE = 0;
/** Error code used when the blob exceeds the preview limit. */
export const BLOB_TOO_LARGE_ERROR_CODE = 413;

/** Message of a failed GET to Azure Storage: status, storage error code and message. */
export const describeAzureReadError = (status: number, responseText?: string | null): string => {
  const body = responseText ?? '';
  const code = body.match(/<Code>([\s\S]*?)<\/Code>/)?.[1]?.trim();
  const detail = body.match(/<AuthenticationErrorDetail>([\s\S]*?)<\/AuthenticationErrorDetail>/)?.[1]?.trim();
  const message = body.match(/<Message>([\s\S]*?)<\/Message>/)?.[1]?.split('\n')[0]?.trim();
  const parts = [`Azure Storage (${status}${code ? ` ${code}` : ''})`];
  if (detail) parts.push(detail);
  else if (message) parts.push(message);
  return parts.join(': ');
};

/**
 * Reads a (small) text blob straight from Azure with a read SAS (preview of txt/csv/json...).
 * Needs CORS GET enabled on the storage account. Rejects with ErrorType:
 * code 0 on network/CORS failure, 413 when larger than `maxBytes`, the HTTP status otherwise.
 */
export const fetchBlobText = async (sasUrl: string, maxBytes: number): Promise<string> => {
  let response: Response;
  try {
    // No Authorization header: the SAS in the URL is the credential.
    response = await fetch(sasUrl, { method: 'GET' });
  } catch (error: unknown) {
    throw buildError(
      BLOB_NETWORK_ERROR_CODE,
      error instanceof Error && error.message ? error.message : 'Network error reading the file'
    );
  }
  if (!response.ok) {
    const body = await response.text().catch(() => '');
    throw buildError(response.status, describeAzureReadError(response.status, body));
  }
  const declared = Number(response.headers?.get?.('content-length') ?? NaN);
  if (Number.isFinite(declared) && declared > maxBytes) {
    throw buildError(BLOB_TOO_LARGE_ERROR_CODE, 'File too large to preview');
  }
  const text = await response.text();
  if (text.length > maxBytes) {
    throw buildError(BLOB_TOO_LARGE_ERROR_CODE, 'File too large to preview');
  }
  return text;
};

// ---- management (contract §9.2) ----

/** Sends a JSON request to Contracts and returns `data`; rejects with ErrorType (also when succeeded=false). */
const sendStorageRequest = async <T>(
  url: string,
  method: 'POST' | 'DELETE',
  token: string,
  body?: unknown
): Promise<T> => {
  try {
    const response = await fetch(url, {
      method,
      headers: headers(token),
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    if (!response.ok) {
      Sentry.captureException(response);
      throw await toErrorType(response);
    }
    const result = (await response.json().catch(() => undefined)) as IResult<T> | undefined;
    if (result && result.succeeded === false) {
      throw buildError(response.status, result.message || 'Operation failed', result.errors ?? []);
    }
    return result?.data as T;
  } catch (error: unknown) {
    Sentry.captureException(error);
    throw normalizeError(error);
  }
};

/** POST contracts/files/storage/folders → new user folder (409 when it exists). */
export const createFolder = async (
  token: string,
  request: StorageCreateFolderRequest
): Promise<StorageFolderViewModel> => {
  const data = await sendStorageRequest<StorageFolderViewModel>(StorageFoldersURL(), 'POST', token, request);
  const parent = request.parentPath ? `${request.parentPath}/` : '';
  return data ?? { name: request.name, path: `${parent}${request.name}`, isSystem: false };
};

/** POST contracts/files/storage/folders/rename → { oldPath, newPath, movedFiles } (500 with errors on partial failure). */
export const renameFolder = async (
  token: string,
  request: StorageRenameFolderRequest
): Promise<StorageFolderRenameViewModel> =>
  sendStorageRequest<StorageFolderRenameViewModel>(StorageRenameFolderURL(), 'POST', token, request);

/** DELETE contracts/files/storage/files?path= (404 when it does not exist). */
export const deleteFile = async (token: string, path: string): Promise<boolean> =>
  (await sendStorageRequest<boolean>(StorageDeleteFileURL(path), 'DELETE', token)) ?? true;

/** DELETE contracts/files/storage/folders?path= (400 system folder, 409 not empty, 404). */
export const deleteFolder = async (token: string, path: string): Promise<boolean> =>
  (await sendStorageRequest<boolean>(StorageDeleteFolderURL(path), 'DELETE', token)) ?? true;

/** POST contracts/files/storage/sas-token/upload → upload SAS (PUT) for a file inside a folder; blobPath relative. */
export const requestFolderUploadSas = async (
  token: string,
  request: StorageUploadSasRequest
): Promise<UploadSasToken> => {
  const data = await sendStorageRequest<UploadSasToken>(StorageUploadSasURL(), 'POST', token, request);
  if (!data?.sasUrl) throw buildError(0, 'SAS token not received');
  return data;
};
